/**
 * Reads and writes for the dashboard, quota, analytics and settings surfaces.
 *
 * The wire shapes come from `lib/types.ts`, which is the app-wide contract. A
 * few of these screens need more than the base shape carries, so the extras are
 * declared here as **optional** extensions: the page renders the richer version
 * when the server sends it and stays honest when it does not. Each one is listed
 * in the handover notes as a contract request rather than assumed.
 *
 * The active brand rides on the server session, so no brand id is ever sent.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { HandleIdType } from '@gojibasel/shared/types';
import { COMPLETE_LIST_LIMIT } from '../../../components/pagination-model';
import { api, buildQuery } from '../../../lib/api';
import { MANUAL_QUEUE_VISIBLE } from '../../../lib/manual-queue';
import { qk } from '../../../lib/queryClient';
import type {
  AccountAdminState,
  AccountHealthRow as BaseAccountHealthRow,
  AccountObservedState,
  ActionType,
  Brand,
  BrandKnowledge,
  BrandKnowledgeVersion,
  CampaignAnalytics,
  CampaignAnalyticsRow,
  Channel,
  ChannelAccount,
  ChannelReadinessAnswer,
  DiscoveryStatus,
  EngineEvent,
  EngineManifest,
  EnrichmentAnalytics,
  IntegrationSession,
  IsoDate,
  IsoDateTime,
  MailboxConnection,
  MailboxKind,
  OperatorAction,
  OperatorActionMode,
  Paginated,
  PolicyScope,
  QuotaAnalytics as BaseQuotaAnalytics,
  QuotaPolicy,
  QuotaUsageRow as BaseQuotaUsageRow,
  ReconciliationResult,
  SafetySettings,
  ScoutChannel,
  ScoutsAnswer,
  ScoutView,
  Suppression,
  SuppressionReason,
  UserRole,
  Uuid,
} from '../../../lib/types';

export type { Paginated };

// ---------------------------------------------------------------------------
// Quota — GET /analytics/quota
// ---------------------------------------------------------------------------

/**
 * One sending account, one channel, one action, for one day.
 *
 * Extensions on top of the base row, all optional:
 *   - `capBase` / `jitterLow` / `jitterHigh` — the policy ceiling before today's
 *     draw, so the page can explain why the number moved.
 *   - `ceilingThrottled` / `waitingOnCeiling` — whether throughput was limited by
 *     the sending account's own cap rather than by campaign supply.
 *   - `byBrand[].openHolds` — brand attribution for reservations, not just sends.
 */
export interface QuotaUsageRow extends BaseQuotaUsageRow {
  capBase?: number;
  jitterLow?: number;
  jitterHigh?: number;
  ceilingThrottled?: boolean;
  waitingOnCeiling?: number;
  byBrand: Array<{ brandId: Uuid; brandName: string; used: number; openHolds?: number }>;
}

export interface QuotaAnalytics extends BaseQuotaAnalytics {
  rows: QuotaUsageRow[];
  weekly?: QuotaUsageRow[];
  /** Present when the caller may see more than themselves. */
  people?: Array<{ personUserId: Uuid; personName: string; tz?: string }>;
  /** ISO week key the weekly counters are on. */
  week?: string;
  generatedAt?: IsoDateTime;
}

export function useQuotaAnalytics(): UseQueryResult<QuotaAnalytics> {
  return useQuery({
    queryKey: qk.analyticsQuota(),
    queryFn: () => api.get<QuotaAnalytics>('/analytics/quota'),
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Campaign analytics — GET /analytics/campaigns
// ---------------------------------------------------------------------------

export const ANALYTICS_GRAINS = [
  'byCampaign',
  'byMethod',
  'byAsset',
  'bySignalType',
  'bySenderAccount',
] as const;
export type AnalyticsGrain = (typeof ANALYTICS_GRAINS)[number];

/** Optional extras: meetings booked, and a daily series for the line chart. */
export interface AnalyticsRow extends CampaignAnalyticsRow {
  sublabel?: string;
  meetings?: number;
}

export interface AnalyticsResponse extends CampaignAnalytics {
  byCampaign: AnalyticsRow[];
  byMethod: AnalyticsRow[];
  byAsset: AnalyticsRow[];
  bySignalType: AnalyticsRow[];
  bySenderAccount: AnalyticsRow[];
  from?: IsoDate;
  to?: IsoDate;
  totals?: { sent: number; replies: number; positives: number; meetings?: number };
  series?: Array<{ date: IsoDate; sent: number; replies: number; positives: number }>;
}

export function useCampaignAnalytics(days: number): UseQueryResult<AnalyticsResponse> {
  return useQuery({
    queryKey: qk.analyticsCampaigns({ days }),
    queryFn: () => api.get<AnalyticsResponse>(`/analytics/campaigns${buildQuery({ days })}`),
  });
}

// ---------------------------------------------------------------------------
// Enrichment credits — GET /analytics/enrichment
// ---------------------------------------------------------------------------

export interface EnrichmentResponse extends EnrichmentAnalytics {
  /** Alert threshold as a fraction of the budget. Defaults to 0.8 when absent. */
  alertFraction?: number;
  units?: number;
  daily?: Array<{ date: IsoDate; costUsd: number; units: number }>;
}

export function useEnrichment(): UseQueryResult<EnrichmentResponse> {
  return useQuery({
    queryKey: qk.analyticsEnrichment(),
    queryFn: () => api.get<EnrichmentResponse>('/analytics/enrichment'),
  });
}

// ---------------------------------------------------------------------------
// Nightly reconciliation — GET /analytics/reconciliation
// ---------------------------------------------------------------------------

export interface ReconciliationResponse extends ReconciliationResult {
  /** How many counters the run compared, not only the failing ones. */
  countersChecked?: number;
  /** Consecutive clean days. The release gate cares about seven. */
  cleanStreakDays?: number;
  rows: Array<ReconciliationResult['rows'][number] & { counterDay?: IsoDate }>;
}

export function useReconciliation(): UseQueryResult<ReconciliationResponse> {
  return useQuery({
    queryKey: qk.analyticsReconciliation(),
    queryFn: () => api.get<ReconciliationResponse>('/analytics/reconciliation'),
  });
}

// ---------------------------------------------------------------------------
// Account health — GET /analytics/accounts
// ---------------------------------------------------------------------------

/**
 * Extensions: the admin and observed states separately (the board's controls
 * need both), failures in the last day, the freeze threshold, the open-card
 * limit, held reservations, and the integration count.
 */
export interface AccountHealthRow extends BaseAccountHealthRow {
  adminState?: AccountAdminState;
  observedState?: AccountObservedState;
  ownerUserId?: Uuid;
  handle?: string;
  warmupStartedAt?: IsoDateTime | null;
  /** 25, 50, 75 or 100: the share of the cap this account may use today. */
  warmupPercent?: number;
  autoFreezeFraction?: number;
  failures24h?: number;
  lastFailureAt?: IsoDateTime | null;
  lastFailureReason?: string;
  maxOpenCards?: number;
  openReservations?: number;
  integrationSessions?: number;
  maxIntegrationSessions?: number;
  lastActionAt?: IsoDateTime | null;
  /**
   * Where the account stands on the failure circuit breaker's halt ladder.
   * Optional, because a server that predates the ladder sends nothing, and
   * absence reads as "not paused" rather than as a guess either way.
   */
  halt?: AccountHaltStatus;
  /**
   * While somebody is using the account, when that pause ends and who said so:
   * null source for a person, auto:<signal> when the phone saw them. Null or
   * absent while nobody is on it.
   */
  manualUse?: { until: IsoDateTime; source: string | null } | null;
}

/** The breaker's halt ladder for one account, as `/analytics/accounts` sends it. */
export interface AccountHaltStatus {
  halted: boolean;
  /** When the pause in force ends, or null when none is. */
  haltedUntil: IsoDateTime | null;
  /** Which pause it is on, from one, or null when the ladder is at rest. */
  tier: number | null;
  tiers: number;
  ladderMinutes: number[];
  /** When a clean run puts the ladder back to the shortest pause, or null at rest. */
  resetsAt: IsoDateTime | null;
  /** What another trip would do, asked at the moment the current pause ends. */
  nextTrip: { kind: 'halt'; tier: number; minutes: number } | { kind: 'freeze' } | { kind: 'refuse' };
}

export function useAccountHealth(): UseQueryResult<Paginated<AccountHealthRow>> {
  return useQuery({
    queryKey: qk.analyticsAccounts(),
    queryFn: () => api.get<Paginated<AccountHealthRow>>('/analytics/accounts'),
  });
}

export interface AccountHealthUpdate {
  observedState?: AccountObservedState;
  adminState?: AccountAdminState;
  /** Written to the audit entry. */
  reason?: string;
}

export function useSetAccountHealth() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: Uuid; body: AccountHealthUpdate }) =>
      api.post<ChannelAccount>(`/channel-accounts/${id}/health`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['analytics'] });
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
      void client.invalidateQueries({ queryKey: ['cards'] });
    },
  });
}

/**
 * What the sweep endpoint answers with, copied from `InboxSweepWire` in
 * apps/api/src/routes/accounts.ts.
 *
 * CORRECTED 2026-09-13, and the version it replaces is worth recording because
 * nothing caught it. This declared `{ requestedAt, scheduled, skipped }`, which
 * the route has never returned. `api.post<T>` is an unchecked cast, so
 * TypeScript saw nothing, every field read back `undefined`, and the line under
 * the button said "Nothing was queued" in the neutral tone on every press --
 * including a sweep that really had queued one. A green suite and a clean
 * typecheck throughout.
 *
 * So this mirrors the wire by hand and the test beside it compares the two
 * declarations, which is the only thing that can fail when they drift again.
 */
export type InboxSweepStatus =
  | 'scheduled'
  | 'already_open'
  | 'nothing_to_check'
  | 'cadence_reached'
  | 'reservation_denied'
  | 'unavailable';

export interface InboxSweepResult {
  channelAccountId: Uuid;
  channel: string;
  status: InboxSweepStatus;
  /** The card to open, on `scheduled` and `already_open`. Null otherwise. */
  cardId: string | null;
  /** How many threads the card lists. Null when no card came of it. */
  itemCount: number | null;
  /** One sentence written for the person who pressed the button. Rendered as-is. */
  detail: string;
  /** So the age under the button is right without a second round trip. */
  lastInboxSweepAt: IsoDateTime | null;
}

/**
 * Ask for one account's inbox to be swept.
 *
 * A queue write and not a fetch. The device engine is pull-only, so this mints
 * housekeeping work against the account and a phone performs it whenever it
 * next comes for work. Nothing about the inbox has changed when this resolves,
 * which is why `accounts/sweep.ts` owns the wording and says so.
 *
 * Three caches go stale at once. The account row now carries a newer
 * `lastInboxSweepAt`, the card queue has the work in it, and the conversations
 * list is what the sweep eventually feeds, so a stale one would show the
 * operator the same empty thread they pressed the button about.
 */
export function useSweepInbox() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: Uuid }) =>
      api.post<InboxSweepResult>(`/channel-accounts/${input.id}/inbox-sweep`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
      void client.invalidateQueries({ queryKey: ['cards'] });
      void client.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

/**
 * Every sending account the caller may see, on every channel.
 *
 * This is the roster half of "what accounts do I have and what is each one
 * doing on the phone": it answers the first half only. The states on these rows
 * (`adminState`, `observedState`, `effectiveHealth`) are the platform's own
 * opinion, formed here and not on any device, so they may never be used to fill
 * in a device fact the phone has not reported. An account the device has never
 * verified has no device answer, and the honest render is to say that rather
 * than to borrow the account's health as a stand-in.
 *
 * `channel` is also not permission. It says where an account lives, not that
 * anything may be sent from it.
 */
/**
 * Rename a sending account.
 *
 * Its label was accepted once, when it was registered, and never again, so a
 * typo in the name of an identity shown on every campaign screen was permanent.
 * An empty name clears the label and the account shows as its handle.
 */
export function useRenameAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; label: string }) =>
      api.patch<ChannelAccount>(`/channel-accounts/${input.id}`, { label: input.label }),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.channelAccounts() }),
  });
}

/**
 * Say what an account is used for. Admin only on the server.
 *
 * The readiness strip is dropped with the roster, because an account kept for
 * reading changes what every brand can send on.
 */
/**
 * Mark an account as one a campaign has to be confirmed before sending as, or
 * take that off.
 *
 * Null clears it. The note is not a label: it is the sentence somebody reads
 * while they are about to pick the account, so it should say whose account this
 * is and what it is for rather than that it is protected.
 */
export function useSetAccountProtection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; note: string | null }) =>
      api.patch<ChannelAccount>(`/channel-accounts/${input.id}`, { protectedNote: input.note }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
      // Every campaign's sender picker publishes the note, so the warning and
      // the confirmation appear without anybody reloading a campaign page.
      void client.invalidateQueries({ queryKey: ['playbooks'] });
    },
  });
}

/** Save an account's own profile address, or clear it with null. The owner or an admin. */
export function useSetAccountProfileUrl() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; url: string | null }) =>
      api.patch<ChannelAccount>(`/channel-accounts/${input.id}`, { profileUrl: input.url }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
    },
  });
}

/** Turn an account's Salaam greeting on or off. The owner or an admin. */
export function useSetAccountUsesSalaam() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; on: boolean }) =>
      api.patch<ChannelAccount>(`/channel-accounts/${input.id}`, { usesSalaam: input.on }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
    },
  });
}

/** Say which paid LinkedIn seat an account has. The owner or an admin. */
export function useSetAccountInMailSeat() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; salesNav?: boolean; premium?: boolean }) =>
      api.patch<ChannelAccount>(`/channel-accounts/${input.id}`, {
        inmailSeat: {
          ...(input.salesNav === undefined ? {} : { salesNav: input.salesNav }),
          ...(input.premium === undefined ? {} : { premium: input.premium }),
        },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
    },
  });
}

export function useSetAccountPurpose() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; purpose: 'outreach' | 'enrichment' | 'both' }) =>
      api.patch<ChannelAccount>(`/channel-accounts/${input.id}`, { purpose: input.purpose }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
      void client.invalidateQueries({ queryKey: ['channel-readiness'] });
      // Named even though the roster root already covers it. What an account
      // is used for decides whether it may be offered as a scout at all.
      void client.invalidateQueries({ queryKey: qk.scouts() });
    },
  });
}

/** The scout picked for each app, and every account that could be one. */
export function useScouts(): UseQueryResult<ScoutsAnswer> {
  return useQuery({
    queryKey: qk.scouts(),
    queryFn: ({ signal }) => api.get<ScoutsAnswer>('/accounts/scouts', { signal }),
  });
}

/**
 * Pick the scout for one app, or clear it with null. Admin only on the server.
 *
 * The readiness strip is dropped along with the scouts, because its reading
 * line names the accounts profiles are read through.
 */
export function useSetScout() {
  const client = useQueryClient();
  return useMutation(scoutPickMutation(client));
}

/** What `useSetScout` hands `useMutation`, apart so the pending state can be tested without React. */
export function scoutPickMutation(client: QueryClient) {
  return {
    mutationFn: (input: { channel: ScoutChannel; channelAccountId: string | null }) =>
      api.put<ScoutView>(`/accounts/scouts/${input.channel}`, { channelAccountId: input.channelAccountId }),
    // The scouts read back is returned, not voided, so the save stays pending
    // until the new row has landed. Voided, pending ended first and the picker
    // showed the old scout for the length of the refetch. The readiness strip is
    // not waited on, because the picker does not read it.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['channel-readiness'] });
      return client.invalidateQueries({ queryKey: qk.scouts() });
    },
  };
}

/**
 * Whether profiles are read on a schedule or only when somebody asks.
 *
 * Read from the server rather than assumed, because the copy that described
 * scans moving people between ways in had no way to know the schedule was off.
 */
export function useDiscoveryStatus(): UseQueryResult<DiscoveryStatus> {
  return useQuery({
    queryKey: ['discovery-status'],
    queryFn: () => api.get<DiscoveryStatus>('/discovery-status'),
    // A deployment setting, which changes with a restart and not by the minute.
    staleTime: 5 * 60_000,
  });
}

/** What one brand can send on right now, per channel. */
export function useChannelReadiness(brandId: string | undefined): UseQueryResult<ChannelReadinessAnswer> {
  return useQuery({
    queryKey: ['channel-readiness', brandId ?? ''],
    queryFn: () => api.get<ChannelReadinessAnswer>(`/brands/${brandId}/channel-readiness`),
    enabled: Boolean(brandId),
    // A phone reading ages by the minute, so the strip refreshes itself rather
    // than showing a reading older than the one it says it is.
    refetchInterval: 60_000,
  });
}

/**
 * The whole roster of sending accounts.
 *
 * ASKED FOR WHOLE, NOT PAGED, and that is a decision rather than an oversight.
 * Every caller either offers these as options or groups something else by them,
 * and a page of a roster is not a smaller roster, it is a picker missing
 * choices and a grouping missing rows. It sent no limit at all until
 * 2026-09-09, which meant the server's default of 50, which meant this
 * deployment was eight accounts away from options quietly disappearing.
 *
 * Past the ceiling the panels that render it say so rather than dropping rows
 * in silence. See `shortfallNote`.
 */
export function useChannelAccounts(): UseQueryResult<Paginated<ChannelAccount>> {
  return useQuery({
    queryKey: [...qk.channelAccounts(), COMPLETE_LIST_LIMIT],
    queryFn: () =>
      api.get<Paginated<ChannelAccount>>(`/channel-accounts${buildQuery({ limit: COMPLETE_LIST_LIMIT })}`),
  });
}

// ---------------------------------------------------------------------------
// Dashboard counts, composed from the queues themselves
// ---------------------------------------------------------------------------

export interface DashboardCard {
  id: Uuid;
  kind: 'action' | 'inbox_check' | 'connections_check';
  channel: Channel;
  actionType: ActionType;
  channelAccountId: Uuid;
  channelAccountLabel?: string;
  brandName?: string;
  prospectName?: string | null;
  prospectHeadline?: string | null;
  openedAt?: IsoDateTime;
  createdAt?: IsoDateTime;
  expiresAt?: IsoDateTime;
}

export function useOpenCards(limit = 6): UseQueryResult<Paginated<DashboardCard>> {
  return useQuery({
    queryKey: qk.cards({ limit }),
    queryFn: () => api.get<Paginated<DashboardCard>>(`/cards${buildQuery({ limit })}`),
    /*
     * Disabled, not deleted, while the work queue is hidden. Both callers are
     * surfaces that draw nothing right now, the sidebar badge and the two
     * dashboard panels, and a disabled query holds its place in the hook order
     * so neither caller has to grow a branch around calling it at all. The data
     * stays `undefined`, which is the same state those callers already handle
     * for a request in flight. See `lib/manual-queue.ts`.
     */
    enabled: MANUAL_QUEUE_VISIBLE,
  });
}

export interface DashboardApproval {
  id: Uuid;
  campaignName?: string;
  brandName?: string;
  prospectName?: string;
  channel: Channel;
  actionType: ActionType;
  preview?: string;
  body?: string;
  assetName?: string | null;
  createdAt?: IsoDateTime;
}

export function usePendingApprovals(limit = 5): UseQueryResult<Paginated<DashboardApproval>> {
  return useQuery({
    queryKey: qk.approvals({ limit }),
    queryFn: () => api.get<Paginated<DashboardApproval>>(`/approvals${buildQuery({ limit })}`),
  });
}

export interface DashboardConversation {
  id: Uuid;
  prospectName?: string;
  brandName?: string;
  campaignName?: string | null;
  channel: Channel;
  senderAccountLabel?: string;
  classification?: string | null;
  lastMessageAt?: IsoDateTime;
  lastMessagePreview?: string;
  snippet?: string;
}

export function usePositiveReplies(limit = 5): UseQueryResult<Paginated<DashboardConversation>> {
  return useQuery({
    queryKey: qk.conversations({ classification: 'POSITIVE', handled: false, limit }),
    queryFn: () =>
      api.get<Paginated<DashboardConversation>>(
        `/conversations${buildQuery({ classification: 'POSITIVE', handled: false, limit })}`,
      ),
  });
}

// ---------------------------------------------------------------------------
// Brands, members, knowledge
// ---------------------------------------------------------------------------

export interface BrandRow extends Brand {
  ownerName?: string;
  activeCampaigns?: number;
  tz?: string;
}

export function useBrands(): UseQueryResult<Paginated<BrandRow>> {
  return useQuery({ queryKey: qk.brands(), queryFn: () => api.get<Paginated<BrandRow>>('/brands') });
}

/**
 * Delete a brand outright.
 *
 * Refused by the API while the brand still holds people, a suppression, a
 * campaign or anything ever sent, and the refusal names what is in the way. So
 * the screen does not need its own copy of that rule: it shows the sentence the
 * server wrote, which is the only version that cannot fall out of date with the
 * guard it describes.
 */
export function useDeleteBrand() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (brandId: string) => api.del<{ id: string; deleted: boolean }>(`/brands/${brandId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.brands() });
      // The brand menu in the header reads the session, not the brand list.
      void client.invalidateQueries({ queryKey: ['session'] });
    },
  });
}

export interface BrandMember {
  userId: Uuid;
  name: string;
  email: string;
  avatarUrl?: string | null;
  role: UserRole;
  addedAt?: IsoDateTime;
}

/** Contract gap: the members list is not in the contract. See the handover notes. */
export function useBrandMembers(brandId: Uuid | null): UseQueryResult<Paginated<BrandMember>> {
  return useQuery({
    queryKey: [...qk.brand(brandId ?? ''), 'members'],
    queryFn: () => api.get<Paginated<BrandMember>>(`/brands/${brandId ?? ''}/members`),
    enabled: brandId !== null,
    retry: false,
  });
}

export function useKnowledge(brandId: Uuid | null): UseQueryResult<BrandKnowledge> {
  return useQuery({
    queryKey: qk.knowledge(brandId ?? ''),
    queryFn: () => api.get<BrandKnowledge>(`/brands/${brandId ?? ''}/knowledge`),
    enabled: brandId !== null,
  });
}

export interface KnowledgeVersionRow extends BrandKnowledgeVersion {
  byteSize?: number;
}

export function useKnowledgeVersions(brandId: Uuid | null): UseQueryResult<Paginated<KnowledgeVersionRow>> {
  return useQuery({
    queryKey: qk.knowledgeVersions(brandId ?? ''),
    queryFn: () => api.get<Paginated<KnowledgeVersionRow>>(`/brands/${brandId ?? ''}/knowledge/versions`),
    enabled: brandId !== null,
  });
}

export function useSaveKnowledge(brandId: Uuid | null) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (rawMd: string) =>
      api.post<BrandKnowledge>(`/brands/${brandId ?? ''}/knowledge`, { rawMd }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.brands() });
      void client.invalidateQueries({ queryKey: qk.session() });
    },
  });
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

export type PolicyProvenance = PolicyScope | 'default';

/** Which scope supplied each resolved number, so an operator can see the source. */
export interface PolicyProvenanceMap {
  capDaily: PolicyProvenance;
  capWeekly: PolicyProvenance;
  minGapMinutes: PolicyProvenance;
  jitter: PolicyProvenance;
}

export interface EffectivePolicy {
  channel: Channel;
  actionType: ActionType;
  capDaily: number;
  capWeekly: number | null;
  minGapMinutes: number;
  jitterLo: number;
  jitterHi: number;
  /** The range a day's cap is drawn from. Shown instead of the stored cap. */
  dayRange?: { low: number; high: number } | null;
  provenance?: PolicyProvenanceMap;
  policyVersion?: number;
}

export interface PolicySubject {
  id: Uuid;
  label: string;
}

export interface PoliciesResponse extends Paginated<QuotaPolicy> {
  /** The resolved numbers for the person and account asked about. */
  effective?: EffectivePolicy[];
  subjects?: { people: PolicySubject[]; accounts: PolicySubject[]; brands: PolicySubject[] };
}

/**
 * The written policy rows, and the resolved numbers for whoever is asked about.
 *
 * This deployment had exactly 50 rows against a page size of 50 when the pager
 * went in, so the fifty-first would have been the first one to vanish. Nothing
 * would have said so: the table drew what it was sent.
 */
export function usePolicies(params: {
  personUserId?: Uuid | null;
  channelAccountId?: Uuid | null;
  limit?: number;
  cursor?: string;
}): UseQueryResult<PoliciesResponse> {
  return useQuery({
    queryKey: [
      ...qk.policies(),
      params.personUserId ?? null,
      params.channelAccountId ?? null,
      params.limit ?? 0,
      params.cursor ?? '',
    ],
    queryFn: () =>
      api.get<PoliciesResponse>(
        `/policies${buildQuery({
          personUserId: params.personUserId ?? undefined,
          channelAccountId: params.channelAccountId ?? undefined,
          limit: params.limit,
          cursor: params.cursor,
        })}`,
      ),
  });
}

export interface PolicyWrite {
  id?: Uuid;
  scope: PolicyScope;
  subjectId: Uuid | null;
  channel: Channel;
  actionType: ActionType;
  capDaily: number;
  capWeekly: number;
  minGapMinutes: number;
  jitterLo: number;
  jitterHi: number;
}

export function useSavePolicy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PolicyWrite) => api.put<QuotaPolicy>('/policies', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.policies() });
      void client.invalidateQueries({ queryKey: ['analytics', 'quota'] });
    },
  });
}

export interface SafetySettingsResponse extends SafetySettings {
  /** Which scope supplied each key. Keys absent from the map came from the seed. */
  provenance?: Partial<Record<keyof SafetySettings, 'global' | 'brand' | 'account'>>;
  defaults?: Partial<SafetySettings>;
}

/**
 * The resolved safety values for the active brand, or for one account when one
 * is named. Keyed under the same root either way, so a save refreshes both.
 */
export function useSafetySettings(accountId: Uuid | null = null): UseQueryResult<SafetySettingsResponse> {
  return useQuery({
    queryKey: accountId === null ? qk.safetySettings() : [...qk.safetySettings(), accountId],
    queryFn: () =>
      api.get<SafetySettingsResponse>(
        '/safety-settings',
        accountId === null ? undefined : { params: { accountId } },
      ),
  });
}

export function useSaveSafetySettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<SafetySettings> & { scope: SafetySettings['scope']; subjectId?: Uuid | null }) =>
      api.put<SafetySettings>('/safety-settings', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.safetySettings() });
      void client.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Integration sessions
// ---------------------------------------------------------------------------

/**
 * One registered tool, plus the extras only this surface asks for.
 *
 * The device-status fields (`deviceActive`, `identityVerifiedAs`,
 * `identityVerifiedAt`, `blockedReason`) come from the base type and are
 * documented there. What matters here is that `lastSeenAt` is what dates them.
 * `identityVerifiedAt` is the only device fact carrying its own instant, so the
 * other three are exactly as old as the check-in that delivered them, and
 * `lastSeenAt` is the check-in. Render none of the three without it.
 */
export interface IntegrationSessionRow extends IntegrationSession {
  /**
   * When the device last talked to the platform.
   *
   * Absent and null mean the same thing, which is that no check-in has been
   * recorded. Neither means the phone is fine and neither may be softened into
   * a dash: an account with no check-in is the case an operator most needs to
   * see, because that is how a dead phone looks from this side.
   */
  lastSeenAt?: IsoDateTime | null;
  /** True when this row only exists because an admin overrode the limit. */
  viaOverride?: boolean;
  overrideReason?: string | null;
  maxPerAccount?: number;
}

/**
 * How often to re-read the sessions list while a screen stays open.
 *
 * This is a display concern and not one of the sending numbers that has to
 * resolve through policy: it changes nothing on any phone and gates nothing.
 *
 * It exists because the check-in facts on these rows arrive by push on the
 * engine's schedule, and the console default leaves a mounted query sitting on
 * its first answer. Without it a screen opened before a phone died would keep
 * showing the check-in from before, and would keep showing it as the newest
 * thing there is. Refetching does not make anything live: it only asks the
 * platform whether the device has said anything since.
 */
const SESSION_REFRESH_MS = 60_000;

/**
 * Every tool registered against a sending account.
 *
 * WHOLE RATHER THAN PAGED, for the same reason as the roster above and with a
 * sharper consequence. The panel groups these by account and compares the count
 * against a per-account ceiling. A page of them makes a count too low, and a
 * count that is too low against a ceiling tells somebody there is room to
 * register another tool on an account that is already full.
 */
export function useIntegrationSessions(): UseQueryResult<Paginated<IntegrationSessionRow>> {
  return useQuery({
    queryKey: [...qk.integrationSessions(), COMPLETE_LIST_LIMIT],
    queryFn: () =>
      api.get<Paginated<IntegrationSessionRow>>(
        `/integration-sessions${buildQuery({ limit: COMPLETE_LIST_LIMIT })}`,
      ),
    refetchInterval: SESSION_REFRESH_MS,
  });
}

/**
 * Sign a sending account in or out of this platform.
 *
 * Invalidates the accounts list and the sessions list together, because signing
 * out changes both: the account's own state, and the device registrations that
 * claimed it. Leaving the sessions cache alone would show a phone still holding
 * an account the platform has just stopped using.
 */
export function useSetAccountSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { channelAccountId: Uuid; signedIn: boolean; reason?: string }) =>
      api.post<ChannelAccount>(`/channel-accounts/${input.channelAccountId}/session`, {
        signedIn: input.signedIn,
        ...(input.reason === undefined || input.reason === '' ? {} : { reason: input.reason }),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
      void client.invalidateQueries({ queryKey: qk.integrationSessions() });
    },
  });
}

/** Mark an account as in use by hand until a time, or free it again with null. */
export function useSetAccountManualUse() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { channelAccountId: Uuid; until: string | null; note?: string }) =>
      api.put<ChannelAccount>(`/channel-accounts/${input.channelAccountId}/manual-use`, {
        until: input.until,
        ...(input.note === undefined || input.note === '' ? {} : { note: input.note }),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
    },
  });
}

export interface IntegrationSessionWrite {
  channelAccountId: Uuid;
  toolName: string;
  adminOverride?: boolean;
  overrideReason?: string;
}

export function useAddIntegrationSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: IntegrationSessionWrite) =>
      api.post<IntegrationSessionRow>('/integration-sessions', body),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.integrationSessions() }),
  });
}

export function useRemoveIntegrationSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => api.del<void>(`/integration-sessions/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.integrationSessions() }),
  });
}

// ---------------------------------------------------------------------------
// Engine events — GET /engine-events
// ---------------------------------------------------------------------------

/**
 * The feed's filters, all optional. An absent one is left out of the query
 * string rather than sent empty, because absent and empty mean different things
 * here: no `acknowledged` filter means both states, while `acknowledged=false`
 * asks only for the rows nobody has cleared. `false` is a real value and reaches
 * the wire, so this must never be narrowed with a truthiness check.
 */
export type EngineEventFilters = {
  acknowledged?: boolean;
  kind?: string;
  channelAccountId?: Uuid;
  limit?: number;
};

/**
 * How often an open log asks for what has happened since.
 *
 * Fifteen seconds. Short enough that somebody watching a campaign sees an event
 * while it is still the thing they are watching for, and long enough that a
 * screen left open on a second monitor is four requests a minute rather than a
 * poll loop.
 */
const LIVE_LOG_REFRESH_MS = 15_000;

export function useEngineEvents(
  filters?: EngineEventFilters & { cursor?: string },
): UseQueryResult<Paginated<EngineEvent>> {
  return useQuery({
    queryKey: qk.engineEvents(filters),
    queryFn: () =>
      api.get<Paginated<EngineEvent>>(
        `/engine-events${buildQuery({
          acknowledged: filters?.acknowledged,
          kind: filters?.kind,
          channelAccountId: filters?.channelAccountId,
          limit: filters?.limit,
          cursor: filters?.cursor,
        })}`,
      ),
    // Live for the same reason the audit log is, and with more force: an engine
    // event is a phone saying something went wrong, and the panel exists to be
    // watched while that is still true. Paged history is fixed and is not
    // refetched.
    refetchInterval: filters?.cursor === undefined || filters.cursor === '' ? LIVE_LOG_REFRESH_MS : false,
  });
}

/**
 * Mark one event seen. The route is idempotent, so acknowledging a row that is
 * already acknowledged is a success and needs no guard on this side.
 *
 * The response is typed `unknown` deliberately: the route answers with the
 * updated row, but the wrapper around it is the API's to choose and this hook
 * refetches the list rather than reading it.
 */
export function useAcknowledgeEngineEvent() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: Uuid }) => api.post<unknown>(`/engine-events/${id}/acknowledge`),
    // The bare domain sweeps every filtered variant, which matters because an
    // acknowledgement moves the row out of the unacknowledged view it was in.
    onSuccess: () => void client.invalidateQueries({ queryKey: ['engine-events'] }),
  });
}

// ---------------------------------------------------------------------------
// What each engine says it can do — GET /engine-manifests
// ---------------------------------------------------------------------------

/**
 * The manifests, newest report per engine.
 *
 * Refetched on the same cadence as the sessions list and for the same reason:
 * every fact on these rows arrived by push on the engine's schedule, and this
 * side can never ask a phone anything. The refresh does not make the page live,
 * it only asks the platform whether an engine has said anything since.
 *
 * An empty `items` is a real answer and means no engine has described itself.
 * Nothing here may be filled in from a verb table written on this side.
 */
export function useEngineManifests(): UseQueryResult<Paginated<EngineManifest>> {
  return useQuery({
    queryKey: qk.engineManifests(),
    queryFn: () => api.get<Paginated<EngineManifest>>('/engine-manifests'),
    refetchInterval: SESSION_REFRESH_MS,
  });
}

/**
 * Ask a handset what is signed in on it.
 *
 * WHAT THIS DOES NOT DO IS WAIT. The transport is pull-only, so this leaves a
 * request on the platform and returns. The engine finds it on its own cycle, and
 * the scan then queues behind whatever the phone is already doing, because it
 * takes the same lock every send takes. So the honest answer to "when" is not
 * here: it is the manifest's own `sweptAt`, which the caller compares against
 * `requestedAt` to know whether what is on screen is older than the last ask.
 *
 * Both queries are invalidated on success. The request list dates the ask, and
 * the manifest carries the answer, and a screen showing a new ask beside an old
 * answer is the whole point.
 */
export function useRequestDeviceScan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (engineId: string) =>
      api.post<{ engineId: string; requestedAt: string }>(
        `/engines/${encodeURIComponent(engineId)}/sweep`,
        {},
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.engineSweepRequests() });
      void client.invalidateQueries({ queryKey: qk.engineManifests() });
    },
  });
}

export interface EngineSweepRequest {
  engineId: string;
  requestedAt: string;
  /** When the engine acknowledged it. Null separates a busy phone from a dead engine. */
  seenAt: string | null;
}

export function useEngineSweepRequests(): UseQueryResult<{ items: EngineSweepRequest[] }> {
  return useQuery({
    queryKey: qk.engineSweepRequests(),
    queryFn: () => api.get<{ items: EngineSweepRequest[] }>('/engine-sweep-requests'),
    refetchInterval: SESSION_REFRESH_MS,
  });
}

// ---------------------------------------------------------------------------
// Operator actions — /operator-actions
// ---------------------------------------------------------------------------

/**
 * The list's filters, both optional.
 *
 * An absent filter is left out of the query string rather than sent empty. The
 * two are different requests: no `channelAccountId` asks for every identity,
 * while `channelAccountId=` asks for the rows belonging to an account with no
 * id, which is a question with no honest answer and a server free to read
 * either way.
 */
export type OperatorActionFilters = {
  channelAccountId?: Uuid;
  limit?: number;
  cursor?: string;
};

/**
 * The request path for a filtered list.
 *
 * Pulled out of the hook so the absent-versus-empty rule above is checkable
 * without a browser. `buildQuery` drops undefined, null and empty string, so an
 * unset filter never reaches the wire.
 */
export function operatorActionsPath(filters?: OperatorActionFilters): string {
  return `/operator-actions${buildQuery({
    channelAccountId: filters?.channelAccountId,
    limit: filters?.limit,
    cursor: filters?.cursor,
  })}`;
}

/**
 * Recent operator actions, newest first.
 *
 * Polled on the shared cadence because the interesting transitions happen off
 * this screen: a queued action becomes claimed when the phone next asks for
 * work, and the phone asks on its own schedule. Without the refetch a page left
 * open would keep showing `queued` for something the engine finished minutes
 * ago, and would keep showing it as the newest thing there is.
 */
export function useOperatorActions(
  filters?: OperatorActionFilters,
): UseQueryResult<Paginated<OperatorAction>> {
  return useQuery({
    // The filters go in as they came, undefined included, because the key hash
    // drops an undefined value. That keeps "no filter" and "this filter left
    // out" on one cache entry, which is the same distinction the query string
    // above draws and it would be strange for the two to disagree.
    queryKey: qk.operatorActions(filters ?? {}),
    queryFn: () => api.get<Paginated<OperatorAction>>(operatorActionsPath(filters)),
    refetchInterval: SESSION_REFRESH_MS,
  });
}

/**
 * One device action, asked for by a named person.
 *
 * `brandId` is the one body in this file that names a brand, and it is not an
 * exception to the rule that the active brand rides on the session. An operator
 * action has no campaign behind it to say which brand's identity and ledger it
 * lands on, so the request says, and the server still decides whether the caller
 * may act for that brand. Send the active brand.
 *
 * `mode` has no default here on purpose. A missing mode must be a rejected
 * request rather than a guess, and the guess that would have been convenient is
 * the one that sends.
 *
 * `confirmTargetName` is what the person typed. It is only meaningful for the
 * verbs where the engine reads the target's name off the control it is about to
 * press, and asking for it anywhere else would imply a check that does not
 * happen.
 */
export interface OperatorActionRequest {
  brandId: Uuid;
  channelAccountId: Uuid;
  actionType: ActionType;
  payload: Record<string, unknown>;
  mode: OperatorActionMode;
  confirmTargetName?: string;
}

/**
 * The caches an operator action moves, whichever way it went.
 *
 * The bare domain sweeps every filtered variant of the list, which matters
 * because a new action belongs at the top of a list the caller may be holding
 * under a narrower filter than the one it was created under.
 *
 * Quota is in here because an operator send is not a second budget. It consumes
 * the identity's allowance exactly as campaign work does, so the numbers on the
 * quota screen move when one is created and move back when one is cancelled.
 * The sweep stops at `analytics/quota` rather than all of `analytics`, because
 * campaign performance and enrichment spend did not change.
 *
 * Exported so the invalidation can be tested against a real cache rather than
 * asserted by reading the hook.
 */
export async function invalidateOperatorActionQueries(client: QueryClient): Promise<void> {
  await Promise.all([
    client.invalidateQueries({ queryKey: ['operator-actions'] }),
    client.invalidateQueries({ queryKey: ['analytics', 'quota'] }),
  ]);
}

/**
 * Ask for one device action.
 *
 * A refusal is an ordinary answer. Quota, suppression and account health all
 * come back as errors carrying a code, and a caller renders them rather than
 * treating them as a fault. Success means the platform accepted the request and
 * nothing more: the phone picks work up when it next checks in, so the answer to
 * this call is never evidence that anything happened on a device.
 */
export function useRunOperatorAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: OperatorActionRequest) => api.post<OperatorAction>('/operator-actions', body),
    onSuccess: () => void invalidateOperatorActionQueries(client),
  });
}

/**
 * Revoke one that is still waiting.
 *
 * This is the ordinary revoke the platform already has, which is why it works at
 * all: between the click and the phone tapping anything, the claim is the last
 * moment this side can say no. It is not a way to undo something the engine has
 * already carried out, and nothing here issues a second verdict on a result.
 */
export function useCancelOperatorAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: Uuid) => api.post<OperatorAction>(`/operator-actions/${id}/cancel`),
    onSuccess: () => void invalidateOperatorActionQueries(client),
  });
}

// ---------------------------------------------------------------------------
// Which accounts a brand sends from
// ---------------------------------------------------------------------------

/** One brand's pins. A brand absent from the response pinned nothing, so on each platform it may use any account no other brand pinned. */
export interface BrandSendingAccounts {
  brandId: Uuid;
  channelAccountIds: Uuid[];
}

export function useBrandSendingAccounts(): UseQueryResult<{ brands: BrandSendingAccounts[] }> {
  return useQuery({
    queryKey: qk.brandSendingAccounts(),
    queryFn: () => api.get<{ brands: BrandSendingAccounts[] }>('/brand-sending-accounts'),
  });
}

export function useSetBrandSendingAccounts() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: BrandSendingAccounts) =>
      api.put<BrandSendingAccounts>(`/brand-sending-accounts/${body.brandId}`, {
        channelAccountIds: body.channelAccountIds,
      }),
    // The pins decide which brands may use each account, which every account
    // row carries, so the roster is read again with them.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.brandSendingAccounts() });
      void client.invalidateQueries({ queryKey: qk.channelAccounts() });
    },
  });
}

// ---------------------------------------------------------------------------
// Mailboxes
// ---------------------------------------------------------------------------

export function useMailboxes(
  window: { limit: number; cursor?: string } = { limit: 50 },
): UseQueryResult<Paginated<MailboxConnection>> {
  return useQuery({
    queryKey: [...qk.mailboxes(), window.limit, window.cursor ?? ''],
    queryFn: () =>
      api.get<Paginated<MailboxConnection>>(
        `/mailboxes${buildQuery({ limit: window.limit, cursor: window.cursor })}`,
      ),
  });
}

export interface MailboxWrite {
  address: string;
  kind: MailboxKind;
  authKind: 'imap' | 'gmail_oauth';
}

export function useAddMailbox() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: MailboxWrite) => api.post<MailboxConnection>('/mailboxes', body),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.mailboxes() }),
  });
}

// ---------------------------------------------------------------------------
// Suppressions
// ---------------------------------------------------------------------------

/**
 * The do-not-contact list.
 *
 * It only ever grows: every reply, bounce and hand-written exclusion adds a row
 * and nothing removes one, so a page of it is a page of a list that will not
 * stop getting longer.
 */
export function useSuppressions(params: {
  q?: string;
  reason?: string;
  limit?: number;
  cursor?: string;
}): UseQueryResult<Paginated<Suppression>> {
  return useQuery({
    queryKey: qk.suppressions({
      q: params.q ?? '',
      reason: params.reason ?? '',
      limit: params.limit ?? 0,
      cursor: params.cursor ?? '',
    }),
    queryFn: () =>
      api.get<Paginated<Suppression>>(
        `/suppressions${buildQuery({
          q: params.q,
          reason: params.reason,
          limit: params.limit,
          cursor: params.cursor,
        })}`,
      ),
  });
}

export interface SuppressionWrite {
  /** A social handle too, since a handle is an identity key (0118). */
  idType: 'email' | 'linkedin_url' | 'domain' | HandleIdType;
  idValue: string;
  reasonCode: SuppressionReason;
  scope: 'global' | 'brand';
  note?: string;
}

export function useAddSuppression() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: SuppressionWrite) => api.post<Suppression>('/suppressions', body),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}

/**
 * Take one off again.
 *
 * The row is stamped as released rather than deleted, so the record that
 * somebody was suppressed and why survives the lift. The listing only ever
 * showed unreleased rows, so a lifted one simply leaves the table.
 */
export interface AuditEntry {
  id: string;
  at: string;
  actor: string;
  actorName: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
}

/**
 * The brand's activity trail. Written since the first migration, readable since
 * this, and reachable past the first page since 2026-09-09.
 *
 * The window travels in the key as well as in the request. Without that, paging
 * forward serves the held first page from the cache and the screen sits still
 * while the request it just made is thrown away.
 */
export function useAuditLog(params: {
  action?: string;
  entity?: string;
  limit?: number;
  cursor?: string;
}): UseQueryResult<Paginated<AuditEntry>> {
  return useQuery({
    queryKey: ['audit', params.action ?? '', params.entity ?? '', params.limit ?? 0, params.cursor ?? ''],
    queryFn: () =>
      api.get<Paginated<AuditEntry>>(
        `/audit${buildQuery({
          action: params.action,
          entity: params.entity,
          limit: params.limit,
          cursor: params.cursor,
        })}`,
      ),
    /*
     * IT KEEPS UP WITH THE PLATFORM, which is the whole reason to have a log
     * open rather than a report to run. Somebody watching this screen while a
     * campaign runs is watching for the thing that just happened, and a page
     * that only changed when they reloaded it was a page that told them
     * nothing until they thought to ask.
     *
     * ONLY ON THE FIRST PAGE. `cursor` set means somebody has paged back into
     * history, and history does not change: refetching there would spend a
     * request to redraw the same rows, and worse, it would do it under
     * somebody who is reading. The newest page is the only one with anything
     * to add.
     */
    refetchInterval: params.cursor === undefined || params.cursor === '' ? LIVE_LOG_REFRESH_MS : false,
  });
}

export function useReleaseSuppression() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ id: string; released: boolean }>(`/suppressions/${id}/release`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}

// ---------------------------------------------------------------------------
// DSAR
// ---------------------------------------------------------------------------

export interface DsarResult {
  prospectId: Uuid;
  /** Which tables were rewritten and how many rows in each. */
  redacted?: Array<{ table: string; rows: number }>;
  tombstoneWritten?: boolean;
}

export function useDsarDelete() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { prospectId: Uuid; reason: string }) =>
      api.post<DsarResult>('/dsar/delete-person', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['prospects'] });
      void client.invalidateQueries({ queryKey: ['suppressions'] });
    },
  });
}

/**
 * One person from the search, in the field names the list route sends.
 *
 * `fullName` is that name, and it is not a style choice: `GET /prospects`
 * answers with `fullName`, the app-wide `ProspectSummary` in `lib/types.ts`
 * calls it that too, and `api.get` casts rather than parses. A shape here that
 * agreed on the meaning but not the spelling would be undefined at runtime on
 * every row, which is a blank name over an erase button and a confirmation box
 * that can never match.
 */
export interface ProspectMatch {
  id: Uuid;
  fullName: string;
  email?: string | null;
  company?: string | null;
  title?: string | null;
}

export function useProspectSearch(q: string): UseQueryResult<Paginated<ProspectMatch>> {
  return useQuery({
    queryKey: qk.prospects({ q, limit: 10 }),
    queryFn: () => api.get<Paginated<ProspectMatch>>(`/prospects${buildQuery({ q, limit: 10 })}`),
    enabled: q.trim().length >= 2,
  });
}
